import {
  Group,
  Mesh,
  BoxGeometry,
  ConeGeometry,
  CylinderGeometry,
  MeshStandardMaterial,
  Box3,
  Vector3,
} from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

const TARGET_LENGTH = 12; // metres — small jet scale
const MODEL_YAW_KEY = 'aircraftModelYawRadV3';
// Default 90° (π/2). The bundled Sketchfab jet's native forward maps to world -X
// at rotation.y = 0 (which appears as screen-right under our chase camera),
// and a +π/2 Y rotation maps that to world +Z = "into the screen" = forward.
const DEFAULT_YAW = Math.PI / 2;

function loadSavedYaw() {
  try {
    const v = localStorage.getItem(MODEL_YAW_KEY);
    if (v == null) return DEFAULT_YAW;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : DEFAULT_YAW;
  } catch {
    return DEFAULT_YAW;
  }
}

export async function loadAircraft() {
  const root = new Group();
  root.name = 'aircraft-root';

  // Inner Group whose rotation.z is the visual bank/roll. Keeping bank on a
  // child means the aircraft's quaternion (yaw + pitch, used for flight
  // direction and chase camera) stays roll-free.
  const rollContainer = new Group();
  rollContainer.name = 'aircraft-roll';
  root.add(rollContainer);
  root.userData.rollContainer = rollContainer;

  try {
    const res = await fetch('/models/jet.glb', { method: 'HEAD' });
    if (!res.ok) throw new Error('not found');

    const gltf = await new GLTFLoader().loadAsync('/models/jet.glb');
    const model = gltf.scene;
    model.rotation.y = loadSavedYaw();
    fitToLength(model, TARGET_LENGTH);
    rollContainer.add(model);
    root.userData.modelInner = model;
  } catch {
    console.warn('[aircraft] /models/jet.glb not found — using primitive fallback');
    rollContainer.add(buildPrimitiveJet());
  }

  // Distance from the aircraft pivot to the lowest point of the model,
  // used by ground clamping so wheels touch the ground (not below it).
  const bbox = new Box3().setFromObject(root);
  root.userData.bellyOffset = Math.max(0, -bbox.min.y);

  return root;
}

// Rotate the loaded glb 90° around Y to help users find the correct facing.
// Returns the new yaw in radians, or null if a primitive jet is in use.
export function rotateModelYaw90(aircraft) {
  const inner = aircraft?.userData?.modelInner;
  if (!inner) return null;
  const TWO_PI = Math.PI * 2;
  inner.rotation.y = ((inner.rotation.y + Math.PI / 2) % TWO_PI + TWO_PI) % TWO_PI;
  try {
    localStorage.setItem(MODEL_YAW_KEY, inner.rotation.y.toString());
  } catch {
    /* localStorage may be unavailable */
  }
  return inner.rotation.y;
}

function fitToLength(obj3d, targetLength) {
  const box = new Box3().setFromObject(obj3d);
  const size = box.getSize(new Vector3());
  const maxAxis = Math.max(size.x, size.y, size.z);
  if (maxAxis > 0) {
    const scale = targetLength / maxAxis;
    obj3d.scale.setScalar(scale);
  }
}

function buildPrimitiveJet() {
  const g = new Group();

  const bodyMat = new MeshStandardMaterial({
    color: 0xd6dde8,
    metalness: 0.65,
    roughness: 0.35,
  });
  const accentMat = new MeshStandardMaterial({
    color: 0x2b3a5a,
    metalness: 0.4,
    roughness: 0.5,
  });
  const glassMat = new MeshStandardMaterial({
    color: 0x1a2238,
    metalness: 0.2,
    roughness: 0.1,
  });

  // Fuselage (cylinder along Z)
  const fuselage = new Mesh(new CylinderGeometry(0.7, 0.7, 8, 24), bodyMat);
  fuselage.rotation.x = Math.PI / 2;
  g.add(fuselage);

  // Nose cone
  const nose = new Mesh(new ConeGeometry(0.7, 2.0, 24), bodyMat);
  nose.rotation.x = Math.PI / 2;
  nose.position.z = 5;
  g.add(nose);

  // Tail cone
  const tail = new Mesh(new ConeGeometry(0.5, 1.6, 16), bodyMat);
  tail.rotation.x = -Math.PI / 2;
  tail.position.z = -4.8;
  g.add(tail);

  // Cockpit canopy
  const canopy = new Mesh(new BoxGeometry(1.0, 0.5, 1.8), glassMat);
  canopy.position.set(0, 0.55, 1.2);
  g.add(canopy);

  // Main wings (swept-back box)
  const wing = new Mesh(new BoxGeometry(8, 0.18, 1.8), bodyMat);
  wing.position.set(0, -0.1, 0);
  g.add(wing);

  // Horizontal stabilizers
  const hStab = new Mesh(new BoxGeometry(3.2, 0.15, 0.9), bodyMat);
  hStab.position.set(0, 0.05, -3.6);
  g.add(hStab);

  // Vertical stabilizer (fin)
  const fin = new Mesh(new BoxGeometry(0.12, 1.2, 1.2), bodyMat);
  fin.position.set(0, 0.7, -3.6);
  g.add(fin);

  // Engines (twin nacelles under wings)
  for (const x of [-2.4, 2.4]) {
    const nacelle = new Mesh(new CylinderGeometry(0.35, 0.35, 1.8, 16), accentMat);
    nacelle.rotation.x = Math.PI / 2;
    nacelle.position.set(x, -0.45, 0);
    g.add(nacelle);
  }

  return g;
}
